/**
 * Supported Languages Configuration
 *
 * All languages spoken in IKEA countries worldwide.
 * Languages are organized by region for clarity.
 */

export interface Language {
  code: string;      // ISO 639-1 code
  name: string;      // English name
  nativeName: string; // Name in the language itself
  rtl?: boolean;     // Right-to-left script
}

export const LANGUAGES: Language[] = [
  // Global
  { code: 'en', name: 'English', nativeName: 'English' },

  // Western Europe
  { code: 'de', name: 'German', nativeName: 'Deutsch' },
  { code: 'fr', name: 'French', nativeName: 'Français' },
  { code: 'nl', name: 'Dutch', nativeName: 'Nederlands' },

  // Southern Europe
  { code: 'es', name: 'Spanish', nativeName: 'Español' },
  { code: 'it', name: 'Italian', nativeName: 'Italiano' },
  { code: 'pt', name: 'Portuguese', nativeName: 'Português' },
  { code: 'el', name: 'Greek', nativeName: 'Ελληνικά' },

  // Nordic
  { code: 'sv', name: 'Swedish', nativeName: 'Svenska' },
  { code: 'no', name: 'Norwegian', nativeName: 'Norsk' },
  { code: 'da', name: 'Danish', nativeName: 'Dansk' },
  { code: 'fi', name: 'Finnish', nativeName: 'Suomi' },

  // Central/Eastern Europe
  { code: 'pl', name: 'Polish', nativeName: 'Polski' },
  { code: 'cs', name: 'Czech', nativeName: 'Čeština' },
  { code: 'hu', name: 'Hungarian', nativeName: 'Magyar' },
  { code: 'ro', name: 'Romanian', nativeName: 'Română' },
  { code: 'sk', name: 'Slovak', nativeName: 'Slovenčina' },
  { code: 'sl', name: 'Slovenian', nativeName: 'Slovenščina' },
  { code: 'hr', name: 'Croatian', nativeName: 'Hrvatski' },
  { code: 'sr', name: 'Serbian', nativeName: 'Српски' },

  // East Asia
  { code: 'ja', name: 'Japanese', nativeName: '日本語' },
  { code: 'ko', name: 'Korean', nativeName: '한국어' },
  { code: 'zh', name: 'Chinese', nativeName: '中文' },

  // Southeast Asia
  { code: 'th', name: 'Thai', nativeName: 'ไทย' },
  { code: 'id', name: 'Indonesian', nativeName: 'Bahasa Indonesia' },
  { code: 'ms', name: 'Malay', nativeName: 'Bahasa Melayu' },

  // South Asia
  { code: 'hi', name: 'Hindi', nativeName: 'हिन्दी' },

  // Middle East
  { code: 'ar', name: 'Arabic', nativeName: 'العربية', rtl: true },
  { code: 'he', name: 'Hebrew', nativeName: 'עברית', rtl: true },
];

// Map of language codes to language objects for quick lookup
export const LANGUAGE_MAP = new Map(LANGUAGES.map(lang => [lang.code, lang]));

// Get language by code
export function getLanguage(code: string): Language | undefined {
  return LANGUAGE_MAP.get(code);
}

// Get language name (native or English)
export function getLanguageName(code: string, useNative = true): string {
  const lang = LANGUAGE_MAP.get(code);
  if (!lang) return code;
  return useNative ? lang.nativeName : lang.name;
}

// Check if language is RTL
export function isRTL(code: string): boolean {
  return LANGUAGE_MAP.get(code)?.rtl ?? false;
}

// Default language
export const DEFAULT_LANGUAGE = 'en';

// Detect browser language and match to supported language
export function detectBrowserLanguage(): string {
  if (typeof navigator === 'undefined') return DEFAULT_LANGUAGE;

  // Get browser languages in order of preference
  const browserLangs = navigator.languages || [navigator.language];

  for (const browserLang of browserLangs) {
    // Try exact match first (e.g., 'en-US' -> 'en')
    const code = browserLang.split('-')[0].toLowerCase();
    if (LANGUAGE_MAP.has(code)) {
      return code;
    }
  }

  return DEFAULT_LANGUAGE;
}
